import {
  type DescribeLabels,
  IMPACT_LABEL,
  SCORE_IMPACTS,
  type ScoreCriterion,
  type ScoreEntity,
  type ScoreImpact,
  WHERE_FIELDS,
  type WhereGroup,
  type WhereRelation,
  type WhereRule,
  criterionLabel,
  describeRule,
  isWhereGroup,
  operatorsFor,
  scoreCriterionSchema,
  whereField,
  whereFieldName,
  whereValueText,
} from '@parcel/filters';

export { IMPACT_LABEL, SCORE_IMPACTS };
export type { ScoreImpact };

export interface EditorState {
  criteria: ScoreCriterion[];
  dirty: boolean;
  /** Criteria shown as Custom condition cards whatever their shape: picked as custom, or loaded as one. */
  custom?: ReadonlySet<string>;
}

export type NewCriterion = Omit<ScoreCriterion, 'id'> & { id?: string };

/** Which card a criterion sits on: one field, one linked record ("Works on projects"), or a custom condition. */
export type CardKind =
  | { type: 'field'; key: string }
  | { type: 'relation'; relation: WhereRelation; negated: boolean }
  | { type: 'custom'; id: string };

/** The rail entries and card titles for each relation, with and without (a negated group). */
export const RELATION_CARD_TITLE: Record<
  ScoreEntity,
  Partial<Record<WhereRelation, { has: string; none?: string }>>
> = {
  account: {
    project: { has: 'Works on projects', none: 'Works on no projects' },
    contact: { has: 'Has contacts', none: 'Has no contacts' },
    signal: { has: 'Has signals', none: 'Has no signals' },
  },
  project: {
    firm: { has: 'Firms on it', none: 'No firm on it' },
    contact: { has: 'Contacts on it', none: 'No contacts on it' },
    signal: { has: 'Has signals', none: 'Has no signals' },
  },
  contact: {
    // Their firm always exists, so there is no negated card.
    firm: { has: 'Their firm' },
    project: { has: 'Works on projects', none: 'Works on no projects' },
  },
};

const onlyRules = (where: WhereGroup): WhereRule[] | null =>
  where.rules.every((node) => !isWhereGroup(node)) ? (where.rules as WhereRule[]) : null;

/** One rule on a base field is a field card; an "all of" group on one relation is that relation's card; else custom. */
export function cardOf(
  entity: ScoreEntity,
  criterion: ScoreCriterion,
  custom?: ReadonlySet<string>,
): CardKind {
  if (custom?.has(criterion.id)) return { type: 'custom', id: criterion.id };
  const rules = onlyRules(criterion.where);
  if (rules && criterion.where.combinator === 'and' && rules.length > 0) {
    const fields = rules.map((r) => whereField(entity, r.field));
    const relations = new Set(fields.map((f) => f?.relation));
    const [relation] = [...relations];
    if (!criterion.where.not && rules.length === 1 && fields[0] && !relation) {
      return { type: 'field', key: rules[0]?.field as string };
    }
    const title = relation ? RELATION_CARD_TITLE[entity][relation] : undefined;
    const negated = criterion.where.not === true;
    if (relations.size === 1 && relation && title && (!negated || title.none)) {
      return { type: 'relation', relation, negated };
    }
  }
  return { type: 'custom', id: criterion.id };
}

export const cardKey = (card: CardKind): string =>
  card.type === 'field'
    ? `field:${card.key}`
    : card.type === 'relation'
      ? `relation:${card.relation}:${card.negated ? 'none' : 'has'}`
      : `custom:${card.id}`;

export function cardTitle(entity: ScoreEntity, card: CardKind, criterion?: ScoreCriterion): string {
  if (card.type === 'field') return whereField(entity, card.key)?.label ?? card.key;
  if (card.type === 'relation') {
    const title = RELATION_CARD_TITLE[entity][card.relation];
    return (card.negated ? title?.none : title?.has) ?? card.relation;
  }
  return criterion?.label?.trim() || 'Custom condition';
}

/** The criteria in cards, each card in the order its first criterion appears. */
export function groupCriteria(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
  custom?: ReadonlySet<string>,
): { key: string; card: CardKind; title: string; criteria: ScoreCriterion[] }[] {
  const groups = new Map<string, { card: CardKind; criteria: ScoreCriterion[] }>();
  for (const criterion of criteria) {
    const card = cardOf(entity, criterion, custom);
    const key = cardKey(card);
    const group = groups.get(key) ?? { card, criteria: [] };
    group.criteria.push(criterion);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, group]) => ({
    key,
    card: group.card,
    title: cardTitle(entity, group.card, group.criteria[0]),
    criteria: group.criteria,
  }));
}

/** A stable id from a label: a-z, 0-9 and _, suffixed on a collision. */
export function newCriterionId(criteria: readonly ScoreCriterion[], base: string): string {
  const slug =
    base
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 32) || 'criterion';
  const taken = new Set(criteria.map((c) => c.id));
  if (!taken.has(slug)) return slug;
  let n = 2;
  while (taken.has(`${slug}_${n}`)) n += 1;
  return `${slug}_${n}`;
}

/** An empty rule on a field: its first operator, and the value that operator starts from. */
export function emptyRule(entity: ScoreEntity, key: string): WhereRule {
  const field = whereField(entity, key);
  const operator = field ? (operatorsFor(field)[0] ?? 'in') : 'in';
  if (operator === 'in' || operator === 'notIn') return { field: key, operator, value: [] };
  if (field?.kind === 'yes_no') return { field: key, operator, value: true };
  return { field: key, operator };
}

const relationFields = (entity: ScoreEntity, relation: WhereRelation) =>
  WHERE_FIELDS[entity].filter((f) => f.relation === relation);

export type CardPick =
  | { type: 'field'; key: string }
  | { type: 'relation'; relation: WhereRelation; negated: boolean }
  | { type: 'custom' };

/** A fresh criterion for a rail pick or a card's "Add more values", at Important. */
export function newCriterionFor(entity: ScoreEntity, pick: CardPick): NewCriterion {
  if (pick.type === 'field') {
    return {
      impact: 'important',
      where: { combinator: 'and', rules: [emptyRule(entity, pick.key)] },
    };
  }
  if (pick.type === 'relation') {
    const first = relationFields(entity, pick.relation)[0];
    return {
      impact: 'important',
      where: {
        combinator: 'and',
        ...(pick.negated ? { not: true } : {}),
        rules: first ? [emptyRule(entity, first.key)] : [],
      },
    };
  }
  const first = WHERE_FIELDS[entity][0];
  return {
    impact: 'important',
    where: { combinator: 'and', rules: first ? [emptyRule(entity, first.key)] : [] },
  };
}

const CUSTOM_ID = /^custom(_\d+)?$/;

/** The editor for a score's stored criteria; a criterion shaped as a custom condition stays one while edited. */
export function editorFor(entity: ScoreEntity, criteria: ScoreCriterion[]): EditorState {
  // A picked Custom condition gets a "custom" id, so it loads back on its card whatever its shape.
  const custom = new Set(
    criteria
      .filter((c) => CUSTOM_ID.test(c.id) || cardOf(entity, c).type === 'custom')
      .map((c) => c.id),
  );
  return { criteria, dirty: false, custom };
}

export function addCriterion(
  state: EditorState,
  criterion: NewCriterion,
  opts: { custom?: boolean } = {},
): EditorState {
  const field = onlyRules(criterion.where)?.[0]?.field.replace(/\./g, '_');
  const base = opts.custom ? 'custom' : (criterion.label ?? (field || 'criterion'));
  const id = criterion.id ?? newCriterionId(state.criteria, base);
  const custom = opts.custom ? new Set([...(state.custom ?? []), id]) : state.custom;
  return {
    ...state,
    dirty: true,
    criteria: [...state.criteria, { ...criterion, id }],
    ...(custom ? { custom } : {}),
  };
}

/** A cleared name is no name: the criterion reads by its rules. Every other label is kept. */
export function updateCriterion(
  state: EditorState,
  id: string,
  patch: Partial<ScoreCriterion>,
): EditorState {
  return {
    ...state,
    dirty: true,
    criteria: state.criteria.map((c) => {
      if (c.id !== id) return c;
      const next = { ...c, ...patch };
      if ('label' in patch && !patch.label?.trim()) {
        const { label: _label, ...unnamed } = next;
        return unnamed;
      }
      return next;
    }),
  };
}

export function removeCriterion(state: EditorState, id: string): EditorState {
  const custom = state.custom ? new Set([...state.custom].filter((c) => c !== id)) : undefined;
  return {
    ...state,
    dirty: true,
    criteria: state.criteria.filter((c) => c.id !== id),
    ...(custom ? { custom } : {}),
  };
}

/** Why each criterion would be refused, by id; the same schema the API runs. */
export function criterionIssues(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
): Map<string, string> {
  const schema = scoreCriterionSchema(entity);
  const issues = new Map<string, string>();
  for (const criterion of criteria) {
    const parsed = schema.safeParse(criterion);
    if (parsed.success) continue;
    const message = parsed.error.issues[0]?.message ?? 'This criterion is incomplete.';
    issues.set(
      criterion.id,
      /at least 1|at least one value|Array must contain/i.test(message)
        ? 'Choose at least one value.'
        : message,
    );
  }
  return issues;
}

/** The criteria the preview can run: every complete one. */
export function previewableCriteria(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
): ScoreCriterion[] {
  const issues = criterionIssues(entity, criteria);
  return criteria.filter((criterion) => !issues.has(criterion.id));
}

/**
 * What a criterion asks for on the hover card: the chips are its first rule's values (any one counts),
 * and the note is every other rule, so "Tier 1" and "Developer" never read as two options.
 */
export function criterionChips(
  entity: ScoreEntity,
  criterion: ScoreCriterion,
  labels: DescribeLabels = {},
): { chips: string[]; note?: string } {
  const rules = onlyRules(criterion.where);
  const [first, ...rest] = rules ?? [];
  if (!rules || !first || criterion.where.combinator !== 'and' || criterion.where.not) {
    return { chips: [criterionLabel(entity, criterion)] };
  }
  const values = Array.isArray(first.value) && first.operator === 'in' ? first.value : null;
  const chips = values
    ? values.map((v) => whereValueText(entity, { ...first, value: [v] }, labels))
    : [ruleChip(entity, first, labels)];
  const note = rest.map((r) => describeRule(entity, r, labels)).join(' · ');
  return note ? { chips, note } : { chips };
}

const ruleChip = (entity: ScoreEntity, rule: WhereRule, labels: DescribeLabels) =>
  describeRule(entity, rule, labels).replace(`${whereFieldName(entity, rule.field)} `, '');

/** The hover card's name for what a criterion reads: its card's title, or a custom criterion's name. */
export function criterionTopic(entity: ScoreEntity, criterion: ScoreCriterion): string {
  const card = cardOf(entity, criterion);
  if (card.type === 'custom') return criterionLabel(entity, criterion);
  return cardTitle(entity, card, criterion);
}
